// textMarkup
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';

/**
 * The TextMarkup component can be used for highlighting text and much more.
 * You use it out of the box for highlighting intervals of text. Otherwise you
 * can specify your own renderer and have some intervals change text color and
 * have others change text style.
 */
export default class TextMarkup extends PureComponent {
	constructor(props) {
		super(props);
		this.textMarkup = React.createRef();
	}

	render() {
		/* eslint-disable no-unused-vars */
		const {
			componentType,
			children,
			text = '',
			highlights: highlightProps,
			onSelectionUpdate,
			defaultHighlightColor = '#f5a5244d',
			style = {},
			className,
			...otherProps
		} = this.props;
		/* eslint-enable no-unused-vars */

		const TextComponent = componentType || 'span'; // was 'article' 
		const intervals = this.generateIntervals();

		const textFragments = intervals.map((interval, i) => {
			const { highlights, text } = interval;
			const highlightValues = highlights.map(x => highlightProps.find(h => h.id === x));
			if (typeof children === 'function') {
				return children(text, highlightValues, i);
			}
			return this.defaultRenderer(text, highlightValues, i);
		});

		return (
			<TextComponent
				{...otherProps}
				style={style} // style={{ whiteSpace: 'pre-wrap', ...style }}
				ref={this.textMarkup}
				className={className}
			>
				{textFragments}
				{/* This allows the last line of the document to be properly
				selected after a triple click. */}
				{/*<br/>*/}
				{/*&nbsp;*/}
			</TextComponent>
		);
	}

	/**
	 * The renderer that is used to display highlights out of the box.
	 * @param text - The text of the current snippet.
	 * @param highlightValues - The values of the highlights contained in the current snippet.
	 * @param i - The index of the interval. (Used for specifying the key prop).
	 * @returns {Element}
	 */
	defaultRenderer(text, highlightValues, i) {
		const { defaultHighlightColor = '#f5a5244d' } = this.props;
		return (
			<span key={i} style={this.generateGradient(highlightValues.map(x => (x && x.color) || defaultHighlightColor))}>
				{text}
			</span>
		);
	}

	/**
	 * Converts the highlights into intervals with lists of highlights.
	 * @returns {[]}
	 */
	generateIntervals = () => {
		const { text, highlights } = this.props;

		// Create a list of start, end points.
		const points = [];
		highlights.forEach(highlight => {
			points.push({
				start: highlight.id,
				value: highlight.start,
			});
			points.push({
				end: highlight.id,
				value: highlight.end,
			});
		});
		points.sort((a, b) => {
			return a.value - b.value;
		});

		// Merge points with the same value together.
		// [{start, end, value},...] => [{start[], end[], value}, ...]
		let uniquePoints = points.reduce((uniquePoints, point) => {
			const { start = [], end = [] } = uniquePoints[point.value] || {};
			if (point.start) {
				start.push(point.start);
			}
			if (point.end) {
				end.push(point.end);
			}
			uniquePoints[point.value] = { start, end };
			return uniquePoints;
		}, {});
		uniquePoints = Object.entries(uniquePoints).map(([value, { start, end }]) => ({ value, start, end }));

		// Covert points to intervals.
		let currentStart = 0;
		let currentHighlights = []; // array of highlight ids.
		let intervals = []; // should look like [{highlights, text}, ...].

		Object.values(uniquePoints).forEach(point => {
			intervals.push({
				highlights: [...currentHighlights],
				text: text.slice(currentStart, point.value),
				id: currentStart,
			});

			point.start.forEach(highlightStartId => {
				currentHighlights.push(highlightStartId);
			});
			point.end.forEach(highlightStartId => {
				currentHighlights.splice(currentHighlights.indexOf(highlightStartId), 1);
			});

			currentStart = point.value;
		});

		// Add the last interval.
		if (currentStart !== text.length - 1) {
			intervals.push({
				highlights: [],
				text: text.slice(currentStart, text.length),
			});
		}

		return intervals;
	};

	/**
	 * A utility to get the start and end values from the selection object.
	 * @returns {{start: *, end: *, sel: *}}
	 */
	getSelectionOffset() {
		let element = this.textMarkup.current;

		// The below code was borrowed from https://stackoverflow.com/a/4812022/9041594
		let start = 0;
		let end = 0;
		const doc = element.ownerDocument || element.document;
		const win = doc.defaultView || doc.parentWindow;
		let sel;
		if (typeof win.getSelection !== 'undefined') {
			sel = win.getSelection();
			if (sel.rangeCount > 0) {
				let range = win.getSelection().getRangeAt(0);
				let preCaretRange = range.cloneRange();
				preCaretRange.selectNodeContents(element);
				preCaretRange.setEnd(range.startContainer, range.startOffset);
				start = preCaretRange.toString().length;
				preCaretRange.setEnd(range.endContainer, range.endOffset);
				end = preCaretRange.toString().length;
			}
		} else if ((sel = doc.selection) && sel.type !== 'Control') {
			let textRange = sel.createRange();
			let preCaretTextRange = doc.body.createTextRange();
			preCaretTextRange.moveToElementText(element);
			preCaretTextRange.setEndPoint('EndToStart', textRange);
			start = preCaretTextRange.text.length;
			preCaretTextRange.setEndPoint('EndToEnd', textRange);
			end = preCaretTextRange.text.length;
		}
		return { start, end, sel };
	}

	/**
	 * Creates a vertical color gradient style object
	 * @param colors[] - An array of CSS colors.
	 * @returns {React style object}
	 */
	generateGradient(colors) {
		return {
			backgroundColor: colors[0],
			backgroundImage: colors.length > 1 ? `linear-gradient(${colors.join(', ')})` : undefined,
		};
	}
}

TextMarkup.propTypes = {
	children: PropTypes.func,
	componentType: PropTypes.elementType,
	defaultHighlightColor: PropTypes.string,
	highlights: PropTypes.array.isRequired,
	style: PropTypes.object,
	text: PropTypes.string,
	className: PropTypes.string,
};
